const NBSP = ' '

export function money(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—'
  // Sub-cent AI costs are real and matter in aggregate; don't round them to $0.00.
  const decimals = value !== 0 && Math.abs(value) < 0.01 ? 4 : digits
  return `$${value.toFixed(decimals)}`
}

const MINOR_SYMBOLS: Record<string, string> = {
  INR: '\u20b9',
  USD: '$',
  EUR: '\u20ac',
  GBP: '\u00a3',
  AED: 'AED\u00a0',
}

/**
 * A price held as integer minor units — paise, cents — rendered for its market.
 *
 * Rupees are grouped the Indian way (\u20b91,00,000, not \u20b9100,000), matching the app so
 * an operator reading a payout beside a coach's own console sees the same
 * number written the same way. The value never becomes a float: dividing paise
 * by 100 into a double and formatting that is how a settlement figure drifts
 * from the ledger it came out of.
 */
export function minor(value: number | null | undefined, currency = 'INR'): string {
  if (value === null || value === undefined) return '\u2014'
  const negative = value < 0
  const units = Math.trunc(Math.abs(value) / 100)
  const digits = String(units)

  let grouped: string
  if (currency === 'INR' && digits.length > 3) {
    const head = digits.slice(0, -3)
    const tail = digits.slice(-3)
    grouped = `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
  } else {
    grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  const symbol = MINOR_SYMBOLS[currency] ?? `${currency}\u00a0`
  return `${negative ? '-' : ''}${symbol}${grouped}`
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('en-US')
}

export function tokens(value: number | null | undefined): string {
  if (!value) return '0'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`
  return String(value)
}

export function percent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

export function ms(value: number | null | undefined): string {
  if (!value) return '—'
  return value >= 1000 ? `${(value / 1000).toFixed(2)}${NBSP}s` : `${value}${NBSP}ms`
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function relative(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const parsed = new Date(iso).getTime()
  if (Number.isNaN(parsed)) return 'never'
  const seconds = Math.round((parsed - Date.now()) / 1000)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
    ['year', Infinity],
  ]
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  let value = seconds
  for (const [unit, span] of units) {
    if (Math.abs(value) < span) return formatter.format(Math.round(value), unit)
    value /= span
  }
  return formatter.format(Math.round(value), 'year')
}

export function initials(name: string, fallback = '?'): string {
  const trimmed = name.trim()
  if (!trimmed) return fallback
  const parts = trimmed.split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || fallback
}

export function titleCase(value: string): string {
  return value
    .replace(/[_.:-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}
